/**
 * NetworkContext — single source of truth for "is the device online?"
 *
 * Native: subscribes to @react-native-community/netinfo.
 * Web:    subscribes to navigator.onLine + window online/offline events.
 *
 * Consumers use `useNetwork().isOffline` to:
 *   - show the global OfflineBanner
 *   - suppress per-screen "failed to load" toasts when we already know
 *     the connection is down (banner covers it)
 */
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Platform } from 'react-native';

interface NetworkState {
  isOffline: boolean;
}

const NetworkContext = createContext<NetworkState>({ isOffline: false });

/** useNetwork - read the current offline state. Defaults to "online" outside the provider. */
export function useNetwork(): NetworkState {
  return useContext(NetworkContext);
}

export function NetworkProvider({ children }: { children: ReactNode }) {
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (typeof navigator === 'undefined' || typeof window === 'undefined') return;
      const update = () => setIsOffline(!navigator.onLine);
      update();
      window.addEventListener('online', update);
      window.addEventListener('offline', update);
      return () => {
        window.removeEventListener('online', update);
        window.removeEventListener('offline', update);
      };
    }

    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      try {
        const NetInfo = (await import('@react-native-community/netinfo')).default;
        if (cancelled) return;
        unsub = NetInfo.addEventListener(state => {
          // isInternetReachable can be null on first event — treat null as "assume online"
          // so we don't false-positive a banner before the probe completes.
          const reachable = state.isInternetReachable;
          const offline = state.isConnected === false || reachable === false;
          setIsOffline(offline);
        });
      } catch {
        // Module not linked yet (e.g. iOS pods not installed) — leave isOffline=false.
      }
    })();

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  return <NetworkContext.Provider value={{ isOffline }}>{children}</NetworkContext.Provider>;
}
